import { useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=\"hidden\"])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "audio[controls]",
  "video[controls]",
  "iframe",
  "[contenteditable=\"true\"]",
  "[tabindex]:not([tabindex=\"-1\"])",
].join(", ");

const isFocusInsideAnotherDialog = (dialogElement) => {
  const focusedDialog = document.activeElement?.closest?.('[aria-modal="true"]');
  return Boolean(focusedDialog) && focusedDialog !== dialogElement;
};

/**
 * Maneja el foco de un diálogo modal: lo lleva al elemento inicial al abrir,
 * lo mantiene adentro con Tab y Shift+Tab, cierra con Escape y lo devuelve
 * al elemento que lo tenía al cerrar.
 *
 * Con diálogos anidados (por ejemplo el visor de imágenes abierto desde el
 * modal de un mensaje) solo reacciona el que tiene el foco, así Escape cierra
 * únicamente el de arriba.
 *
 * @param {React.RefObject<HTMLElement>} dialogRef Elemento con aria-modal.
 * @param {React.RefObject<HTMLElement>} initialFocusRef Elemento que recibe el foco al abrir.
 * @param {() => void} onClose Cierra el diálogo.
 */
export const useModalFocusTrap = (dialogRef, initialFocusRef, onClose) => {
  useEffect(() => {
    const previouslyFocusedElement = document.activeElement;
    initialFocusRef.current?.focus();

    const handleKeyDown = (event) => {
      const dialogElement = dialogRef.current;
      if (!dialogElement || isFocusInsideAnotherDialog(dialogElement)) return;

      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      const focusableElements = Array.from(
        dialogElement.querySelectorAll(FOCUSABLE_SELECTOR),
      );
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const focusIsInside = dialogElement.contains(document.activeElement);

      if (event.shiftKey && (document.activeElement === firstElement || !focusIsInside)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (document.activeElement === lastElement || !focusIsInside)) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElement?.focus?.();
    };
  }, [dialogRef, initialFocusRef, onClose]);
};
